import React from 'react';
import { FileSignature, Download, Send } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

/**
 * ConventionAction — convention block on the Devis detail sheet.
 *
 * Two buttons: download PDF and "Envoyer en signature" (generates a public
 * signature link and emails it to the company contact).
 *
 * Extracted from `pages/Devis.jsx` (Wave 1u3 refactor step 4) — was an
 * inline function definition at the bottom of the 988-line page module.
 */
export default function ConventionAction({ quote }) {
  const [busy, setBusy] = React.useState(false);
  const [busyDl, setBusyDl] = React.useState(false);

  const handleSendSign = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/convention/signature-link`);
      if (data.email_sent) {
        toast({ title: 'Convention envoyée', description: `Lien de signature envoyé à ${data.email_to}` });
      } else if (data.email_to) {
        // On ne devine jamais la raison : « destinataire non vérifié »
        // accusait l'adresse du client alors que la cause était chez nous —
        // aucune boîte d'envoi utilisable. Sans raison, on le dit.
        toast({
          title: 'Lien généré, courriel non parti',
          description: data.email_error
            ? `${data.email_error} — le lien reste valable : ${data.full_url}`
            : `Raison inconnue. Vérifiez la boîte d'envoi dans Paramètres → Email. Le lien reste valable : ${data.full_url}`,
        });
      } else {
        toast({ title: 'Lien généré', description: `Aucun email destinataire. Copiez : ${data.full_url}` });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Action impossible' });
    }
    setBusy(false);
  };

  const handleDownload = async () => {
    if (busyDl) return;
    setBusyDl(true);
    try {
      const res = await api.get(`/quotes/${quote.id}/convention/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `convention-${quote.id}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Téléchargement impossible' });
    }
    setBusyDl(false);
  };

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3 space-y-2" data-testid={`convention-block-${quote.id}`}>
      <div className="flex items-center gap-2">
        <FileSignature className="w-4 h-4 text-[#4F46E5]" />
        <h4 className="text-[12.5px] font-semibold text-[#1A1B2E]">Convention de formation</h4>
        <span className="text-[10.5px] text-[#8E8FA5] ml-auto">liée à ce devis</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`convention-download-${quote.id}`}
          onClick={handleDownload}
          disabled={busyDl}
          className="h-10 rounded-xl border border-[#EFF0F6] bg-white text-[12.5px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB] flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          <Download className="w-3.5 h-3.5" /> {busyDl ? '…' : 'Télécharger'}
        </button>
        <button
          data-testid={`convention-send-sign-${quote.id}`}
          onClick={handleSendSign}
          disabled={busy}
          className="h-10 rounded-xl bg-[#4F46E5] text-white text-[12.5px] font-semibold hover:opacity-95 flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          <Send className="w-3.5 h-3.5" /> {busy ? 'Envoi…' : 'Envoyer en signature'}
        </button>
      </div>
    </div>
  );
}
